import { useEffect, useState } from "react";

type TimeCorrectionSheetProps = {
  open: boolean;
  shiftTimeRecordId: string | null;
  busy?: boolean;
  onSubmit: (payload: {
    shiftTimeRecordId: string;
    correctionType: string;
    proposedValue: Record<string, unknown>;
    staffExplanation: string;
  }) => void;
  onClose: () => void;
};

export function TimeCorrectionSheet({
  open,
  shiftTimeRecordId,
  busy,
  onSubmit,
  onClose,
}: TimeCorrectionSheetProps) {
  const [correctionType, setCorrectionType] = useState("clock_in");
  const [proposedTime, setProposedTime] = useState("");
  const [explanation, setExplanation] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !shiftTimeRecordId) return null;

  const canSubmit = explanation.trim().length > 0 && proposedTime.length > 0;

  return (
    <div className="staff-sheet-backdrop" role="presentation" onClick={onClose}>
      <div className="staff-sheet staff-sheet--tall" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2 className="staff-sheet__title">Request time correction</h2>
        <p className="muted staff-sheet__message">Management will review your proposed change.</p>
        <label className="staff-field">
          <span>What needs correcting?</span>
          <select value={correctionType} onChange={(e) => setCorrectionType(e.target.value)}>
            <option value="clock_in">Clock-in time</option>
            <option value="clock_out">Clock-out time</option>
            <option value="break">Break time</option>
          </select>
        </label>
        <label className="staff-field">
          <span>Corrected time</span>
          <input
            type="datetime-local"
            value={proposedTime}
            onChange={(e) => setProposedTime(e.target.value)}
          />
        </label>
        <label className="staff-field">
          <span>Explanation</span>
          <textarea
            rows={4}
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Describe what happened"
          />
        </label>
        <div className="staff-sheet__actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            disabled={busy || !canSubmit}
            onClick={() =>
              onSubmit({
                shiftTimeRecordId,
                correctionType,
                proposedValue: { correctedAt: new Date(proposedTime).toISOString() },
                staffExplanation: explanation.trim(),
              })
            }
          >
            {busy ? "Sending…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
}
